import React from 'react'
import { Button } from 'reactstrap'
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { withFirebase } from '../Fb'
import PersonSearch from '../Inputs/PersonSearch'
import RolesHelp from '../Infos/RolesHelp'
import { VENUE_ROLES, accountStatus, roleLabel } from '../utils/userRoles'

// Käyttäjät > "Anna rooli henkilölle": pick a person from the venue's register and give them a
// venue role (docs/roles.md, "How roles are given"). The person's id is their login's uid, so
// users-updateRole can set the role directly - no new-user form, and no matter whether they
// already have a customer account.

const personName = (p) => [p.firstname, p.lastname].filter(Boolean).join(' ') || p.name || p.email || ''

function AssignRoleDialog({ open, onClose, firebase }) {
  const [people, setPeople] = React.useState([])
  const [account, setAccount] = React.useState(null)
  const [loadingAccount, setLoadingAccount] = React.useState(false)
  const [role, setRole] = React.useState('cashier')
  const [understood, setUnderstood] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [done, setDone] = React.useState(null)
  const person = people[0]

  const reset = () => {
    setPeople([])
    setAccount(null)
    setRole('cashier')
    setUnderstood(false)
    setError(null)
    setDone(null)
  }
  const close = () => {
    reset()
    onClose()
  }

  const pick = async (name, values) => {
    const picked = (values || []).slice(-1)
    setPeople(picked)
    setAccount(null)
    setUnderstood(false)
    setError(null)
    setDone(null)
    if (!picked[0]) return
    setLoadingAccount(true)
    try {
      const found = await firebase.getUserAccount(picked[0].id)
      setAccount(found)
      // someone with a role here starts from that role
      if (accountStatus(found, firebase.customerid) === 'here') setRole(String(found.role).toLowerCase())
    } catch (e) {
      console.error('Reading the account failed', e)
      setError('Käyttäjätiedon haku epäonnistui.')
    }
    setLoadingAccount(false)
  }

  const status = account ? accountStatus(account, firebase.customerid) : null
  const sameRole = status === 'here' && String(account.role).toLowerCase() === role
  const canSave = person && account && status !== 'none' && !sameRole && (status !== 'elsewhere' || understood) && !saving

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      await firebase.updateUserRole(person.id, role)
      setDone({ name: personName(person), role })
      setPeople([])
      setAccount(null)
    } catch (e) {
      console.error('Giving the role failed', e)
      setError(e.message || 'Roolin antaminen epäonnistui.')
    }
    setSaving(false)
  }

  return (
    <Dialog open={!!open} onClose={saving ? undefined : close} maxWidth='sm' fullWidth>
      <DialogTitle>Anna rooli henkilölle</DialogTitle>
      <DialogContent>
        {done ? (
          <Alert severity='success' sx={{ mb: 2 }}>
            {done.name} on nyt {roleLabel(done.role)}. Rooli tulee voimaan, kun hän kirjautuu Venuemanageriin seuraavan kerran.
          </Alert>
        ) : null}
        <Typography variant='body2' color='text.secondary'>
          Hae henkilö paikan asiakasrekisteristä. Jos henkilöä ei vielä ole rekisterissä, käytä Uusi käyttäjä -painiketta.
        </Typography>
        <PersonSearch label='Henkilö' placeholder='Hae nimellä, sähköpostilla tai puhelinnumerolla' name='person' onlyone showEmail values={people} onChange={pick} />

        {loadingAccount ? <CircularProgress size={18} sx={{ mt: 1 }} /> : null}
        {person && account ? (
          <Box sx={{ mt: 1 }}>
            {status === 'none' ? (
              <Alert severity='warning'>Henkilöllä ei ole käyttäjätunnusta, joten roolia ei voi antaa. Lisää hänet Uusi käyttäjä -painikkeella.</Alert>
            ) : null}
            {status === 'user' ? (
              <Typography variant='body2'>Nykyinen tila: asiakas, ei roolia.</Typography>
            ) : null}
            {status === 'here' ? (
              <Typography variant='body2'>Nykyinen rooli tässä paikassa: {roleLabel(account.role)}.</Typography>
            ) : null}
            {status === 'elsewhere' ? (
              <Alert severity='warning'>
                Henkilöllä on rooli {roleLabel(account.role)} toisessa paikassa. Käyttäjällä voi olla rooli vain yhdessä paikassa, joten se rooli poistuu, kun
                annat roolin täällä.
                <FormControlLabel
                  sx={{ display: 'block', mt: 1 }}
                  control={<Checkbox size='small' checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />}
                  label='Ymmärrän, anna rooli silti'
                />
              </Alert>
            ) : null}
          </Box>
        ) : null}

        {person && account && status !== 'none' ? (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='subtitle2'>Rooli</Typography>
              <RolesHelp />
            </Box>
            <RadioGroup value={role} onChange={(e) => setRole(e.target.value)}>
              {VENUE_ROLES.map((r) => (
                <FormControlLabel
                  key={r.value}
                  value={r.value}
                  control={<Radio size='small' />}
                  label={
                    <span>
                      <strong>{r.label}</strong>
                      <Typography component='span' variant='body2' color='text.secondary'>
                        {' – ' + r.help}
                      </Typography>
                    </span>
                  }
                />
              ))}
            </RadioGroup>
            {sameRole ? (
              <Typography variant='body2' color='text.secondary'>
                Henkilöllä on jo tämä rooli.
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {error ? (
          <Alert severity='error' sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button color='link' onClick={close} disabled={saving}>
          {done ? 'Sulje' : 'Peruuta'}
        </Button>
        <Button color='primary' onClick={save} disabled={!canSave}>
          {saving ? <CircularProgress size={14} color='inherit' /> : 'Anna rooli'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withFirebase(AssignRoleDialog)
